/**
 * Adventure Result Card Component
 * Displays an adventure in search results
 */

import { useState } from 'react'
import { Card, CardContent, CardActionArea, Box, Typography, Chip, Rating, IconButton, Popover } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import {
  LocationOn as LocationIcon,
  People as PeopleIcon,
  CalendarToday as CalendarIcon,
  TrendingUp,
} from '@mui/icons-material'
import type { AdventureSearchResult } from '../../../api/types/search'
import { AdventureMainCategory, ADVENTURE_MAIN_CATEGORY_LABELS } from '../../adventures/types/adventure-editor.types'

interface AdventureResultCardProps {
  adventure: AdventureSearchResult
}

export function AdventureResultCard({ adventure }: AdventureResultCardProps) {
  const navigate = useNavigate()
  const [statsAnchorEl, setStatsAnchorEl] = useState<HTMLElement | null>(null)

  const handleClick = () => {
    navigate(`/adventures/${adventure.id}`)
  }

  const handleStatsClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setStatsAnchorEl(event.currentTarget)
  }

  const handleStatsClose = () => {
    setStatsAnchorEl(null)
  }

  const hasStats = adventure.uniquePlayersLastHour !== undefined ||
    adventure.uniquePlayersLast24Hours !== undefined ||
    adventure.uniquePlayersLastMonth !== undefined ||
    adventure.uniquePlayersLastYear !== undefined

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const getDifficultyLabel = (level: number) => {
    const labels = ['Easy', 'Moderate', 'Challenging', 'Difficult', 'Extreme']
    return labels[level - 1] || 'Unknown'
  }

  // Get the adventure type label based on mainCategory
  const getAdventureTypeLabel = (category: number) => {
    return ADVENTURE_MAIN_CATEGORY_LABELS[category as AdventureMainCategory] || 'Adventure'
  }

  return (
    <Box sx={{ position: 'relative', mb: 2, mt: 3 }}>
      {/* Type Badge - Positioned above card */}
      <Chip
        label={getAdventureTypeLabel(adventure.mainCategory)}
        size="small"
        color="primary"
        sx={{
          position: 'absolute',
          top: -10,
          left: 16,
          zIndex: 1,
        }}
      />
      {/* Stats Icon - Positioned top right */}
      {hasStats && (
        <IconButton
          size="small"
          onClick={handleStatsClick}
          title="View popularity stats"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            backgroundColor: 'background.paper',
            boxShadow: 1,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <TrendingUp fontSize="small" />
        </IconButton>
      )}
      <Card
        sx={{
          mt: 0,
          transition: 'all 0.2s',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 4,
          },
        }}
      >
        <CardActionArea onClick={handleClick}>
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>

          {/* Title */}
          <Typography variant="h6" component="h3" gutterBottom>
            {adventure.title || adventure.name}
          </Typography>

          {/* Description */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {adventure.quickDescription || adventure.description}
          </Typography>

          {/* Metadata Row 1 */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
            {/* Location */}
            {adventure.location && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LocationIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {adventure.location}
                </Typography>
              </Box>
            )}

            {/* Participants */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PeopleIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {adventure.participantCount} participant{adventure.participantCount !== 1 ? 's' : ''}
              </Typography>
            </Box>

            {/* Start Date */}
            {adventure.startDate && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {formatDate(adventure.startDate)}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Metadata Row 2 */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            {/* Difficulty */}
            {adventure.difficultyLevel > 0 && (
              <Chip
                label={getDifficultyLabel(adventure.difficultyLevel)}
                size="small"
                variant="outlined"
              />
            )}

            {/* Rating */}
            {adventure.averageRating > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Rating
                  value={adventure.averageRating}
                  precision={0.5}
                  size="small"
                  readOnly
                />
                <Typography variant="caption" color="text.secondary">
                  ({adventure.ratingCount})
                </Typography>
              </Box>
            )}

            {/* Repeatable Badge */}
            {adventure.isRepeatable && (
              <Chip label="Repeatable" size="small" variant="outlined" color="info" />
            )}
          </Box>

          {/* Creator */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Created by {adventure.creatorName || adventure.createdByUserName}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>

      {/* Stats Popover */}
      <Popover
        open={Boolean(statsAnchorEl)}
        anchorEl={statsAnchorEl}
        onClose={handleStatsClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box sx={{ p: 2, minWidth: 220 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5 }}>
            Adventure Popularity
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Last Hour:</Typography>
              <Typography variant="body2" fontWeight={500}>
                {(adventure.uniquePlayersLastHour ?? 0).toLocaleString()}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Last 24 Hours:</Typography>
              <Typography variant="body2" fontWeight={500}>
                {(adventure.uniquePlayersLast24Hours ?? 0).toLocaleString()}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Last Month:</Typography>
              <Typography variant="body2" fontWeight={500}>
                {(adventure.uniquePlayersLastMonth ?? 0).toLocaleString()}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Last Year:</Typography>
              <Typography variant="body2" fontWeight={500}>
                {(adventure.uniquePlayersLastYear ?? 0).toLocaleString()}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Popover>
    </Box>
  )
}
